import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Banknote,
  Shield,
  Wallet,
  Send,
  Globe,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function LandingPage() {
  return (
    <main className="font-inter text-gray-800">
      <section className="relative overflow-hidden bg-slate-950 py-24 text-white md:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.35),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(250,204,21,0.18),_transparent_30%)]" />
        <div className="relative mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <p className="mb-4 inline-flex rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-cyan-200">
              Built for secure payments, transfers, and digital assets across Africa
            </p>
            <h1 className="max-w-3xl text-5xl font-bold leading-tight md:text-6xl">
              One trusted platform for your money, payments, and crypto.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              Send and receive money, pay bills, manage your wallet, and handle crypto transactions with speed, clarity, and confidence.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 rounded-2xl bg-accent px-8 py-4 font-semibold text-slate-950 transition hover:opacity-90"
              >
                Open Your Account <ArrowRight size={18} />
              </Link>
              <Link
                to="/security"
                className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-8 py-4 font-semibold text-white transition hover:bg-white/10"
              >
                Security & Trust
              </Link>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {[
                "Fast wallet funding and transfers",
                "Transparent transaction tracking",
                "Reliable support and security controls",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
                  <CheckCircle2 className="mt-0.5 text-emerald-300" size={18} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-cyan-200">Operations</p>
                  <h2 className="mt-2 text-2xl font-semibold">Everything in one place</h2>
                </div>
                <TrendingUp className="text-accent" size={28} />
              </div>
              <div className="space-y-3 text-sm text-slate-200">
                <div className="rounded-2xl bg-white/5 p-4">Fund your wallet and monitor every transaction in real time</div>
                <div className="rounded-2xl bg-white/5 p-4">Pay bills and transfer money without switching apps</div>
                <div className="rounded-2xl bg-white/5 p-4">Manage crypto deposits and withdrawals with clear status updates</div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-[2rem] bg-white p-6 text-slate-900 shadow-2xl shadow-blue-950/20">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Trust</p>
                <h3 className="mt-3 text-3xl font-bold">24/7</h3>
                <p className="mt-2 text-sm text-slate-600">Platform monitoring and service updates to keep your experience smooth.</p>
              </div>
              <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 text-white">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-200">Control</p>
                <h3 className="mt-3 text-3xl font-bold">Secure by design</h3>
                <p className="mt-2 text-sm text-slate-300">Built with transaction validation, account protection, and clear operational safeguards.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-primary">Core rails</p>
            <h2 className="text-4xl font-bold text-slate-900">Built for your daily financial life.</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              MekPay brings essential financial tools into one experience so you can move money faster, manage expenses, and stay in control of your funds.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {[
              {
                icon: Wallet,
                title: "Wallet Operations",
                text: "Top up your account, view balances, and track your money with full visibility.",
              },
              {
                icon: Banknote,
                title: "Payout Workflows",
                text: "Request bank withdrawals and follow each payout from request to completion.",
              },
              {
                icon: CreditCard,
                title: "Everyday Payments",
                text: "Pay airtime, data, and utility bills quickly from the same wallet.",
              },
              {
                icon: Send,
                title: "Crypto Actions",
                text: "Access deposit addresses and monitor crypto transactions with clear status updates.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="rounded-[2rem] border border-slate-200 bg-slate-50 p-7 transition hover:-translate-y-1 hover:shadow-xl">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Icon size={24} />
                  </div>
                  <h3 className="mt-5 text-2xl font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-3 text-base leading-7 text-slate-600">{item.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-primary">Why this matters</p>
            <h2 className="text-4xl font-bold text-slate-900">Confidence comes from consistency.</h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              We focus on reliable transactions, clear communication, and strong safeguards so you always know where your money is and what is happening next.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {[
              {
                icon: Shield,
                title: "Account safety",
                text: "Security controls are designed to protect users and keep transactions dependable.",
              },
              {
                icon: Globe,
                title: "Clear policies",
                text: "Our security, privacy, and terms pages explain how the platform works and protects users.",
              },
              {
                icon: Wallet,
                title: "Transaction clarity",
                text: "You can track statuses across transfers, withdrawals, and crypto activity with confidence.",
              },
              {
                icon: CreditCard,
                title: "Responsive support",
                text: "Our operations team monitors requests and works to resolve issues quickly and transparently.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-[2rem] bg-white p-6 shadow-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/25 text-slate-900">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-primary py-16 text-white">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 text-center md:grid-cols-3">
          {[
            { label: "Service model", value: "Multi-service" },
            { label: "Security approach", value: "User-first" },
            { label: "Coverage", value: "Africa-focused" },
          ].map((stat) => (
            <div key={stat.label}>
              <h3 className="text-4xl font-bold">{stat.value}</h3>
              <p className="mt-2 text-white/80">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-4xl rounded-[2.5rem] bg-slate-950 px-8 py-12 text-center text-white shadow-2xl shadow-slate-300/40 md:px-14">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-200">Get started</p>
          <h2 className="mt-4 text-4xl font-bold">Take control of your payments and digital finance.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300">
            Join MekPay to manage your wallet, move money, pay bills, and handle crypto transactions from one secure dashboard.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-2xl bg-accent px-8 py-4 font-semibold text-slate-950 transition hover:opacity-90"
            >
              Get Started <ArrowRight size={18} />
            </Link>
            <Link
              to="/faqs"
              className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-8 py-4 font-semibold text-white transition hover:bg-white/10"
            >
              Read FAQs
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
